import { Capacitor } from '@capacitor/core'
import { datadogRum } from '@datadog/browser-rum'
import type { Router } from 'vue-router'

import { useConfigStore } from '@/store/config'
import { pinia } from '@/store/pinia'

interface RUMLocation {
  id: string
  name: string
}

interface RUM {
  identify(location: RUMLocation): void
  setRouter(router: Router): void
  addError(error: unknown): void
}

class DatadogRUM implements RUM {
  constructor() {
    if (import.meta.env.VITE_DATADOG_CLIENT_TOKEN && Capacitor.getPlatform() !== 'web') {
      datadogRum.init({
        applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.eu',
        service: 'pos',
        env: 'prod',
        version: import.meta.env.VITE_APP_VERSION || 'unknown',
        allowedTracingUrls: [import.meta.env.VITE_APP_BASE_URL],
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: false,
        trackViewsManually: true,
        defaultPrivacyLevel: 'allow',
        allowFallbackToLocalStorage: true,
        beforeSend: event => {
          if (event.type === 'error' && event.error.type === 'NetworkError') {
            return false
          }
          return true
        }
      })
    }
  }

  identify(location: RUMLocation) {
    const { device } = useConfigStore(pinia)
    datadogRum.setUser({
      id: location.id,
      name: location.name,
      deviceId: device.id
    })
  }

  setRouter(router: Router) {
    router.afterEach(to => {
      datadogRum.startView({ name: to.name?.toString() })
    })
  }

  addError(error: unknown) {
    datadogRum.addError(error)
  }
}

const rum: RUM = new DatadogRUM()

export default rum
